// Lazy face + metadata analysis for photos that arrived without it.
//
// Why "lazy": magic-link uploads from non-Safari browsers can't run face-api
// at upload time (HEIC isn't decodable client-side without canvas). Instead
// we run analysis when admin first opens the editor and the photos load.
// Cloudflare Images has already converted HEIC → JPEG by then, so face-api
// just sees a regular JPEG.
//
// Bounded concurrency (2 simultaneous detections) so a 30-photo album
// doesn't pin the browser. Results POST to the existing /photos/:id/metadata
// endpoint which handles the DB update.

import * as faceapi from 'face-api.js';
import { supabase } from './supabase';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';
// 1 on memory-constrained mobile (iOS evicts the editor under memory pressure —
// fewer concurrent detections = fewer big decoded canvases coexisting), 2 on
// desktop where it's safe.
const ANALYSIS_CONCURRENT =
  typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 1 : 2;

let modelsLoaded = false;
async function loadModels(): Promise<void> {
  if (modelsLoaded) return;
  await faceapi.nets.ssdMobilenetv1.loadFromUri('/models');
  modelsLoaded = true;
}

/** Free the face-api model tensors. Called right before the iOS photo picker
 *  opens: iOS discards a heavy backgrounded page while the picker is up and
 *  reloads it on return (losing the selection) — a lighter page survives. The
 *  net re-loads lazily on the next analysis. */
export function releaseModels(): void {
  try { faceapi.nets.ssdMobilenetv1.dispose(); } catch { /* not loaded */ }
  modelsLoaded = false;
}

class Sem {
  private slots: number;
  private q: (() => void)[] = [];
  constructor(n: number) { this.slots = n; }
  async acquire() {
    if (this.slots > 0) { this.slots--; return; }
    return new Promise<void>((r) => this.q.push(r));
  }
  release() {
    if (this.q.length) this.q.shift()!();
    else this.slots++;
  }
}
const sem = new Sem(ANALYSIS_CONCURRENT);

function classifyAspect(w: number, h: number): 'landscape' | 'portrait' | 'square' {
  const r = w / h;
  if (r > 1.05) return 'landscape';
  if (r < 0.95) return 'portrait';
  return 'square';
}

/**
 * 64-bit perceptual hash (dHash variant).
 *
 * Resizes to a 9×8 grayscale grid, then for each row produces 8 bits by
 * comparing each pixel to the next (1 if left > right, else 0). Returns a
 * 16-character hex string. Two photos with Hamming distance < 12 are
 * "look-alikes" — useful for grouping bursts (4 shots of the same scene).
 *
 * Cheap: ~5 ms per image on a phone. No external dep.
 */
function computeDHash(src: HTMLImageElement | HTMLCanvasElement): string {
  const W = 9, H = 8;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return '';
  ctx.drawImage(src, 0, 0, W, H);
  const data = ctx.getImageData(0, 0, W, H).data;

  // Grayscale (Rec. 601 luma)
  const gray = new Uint8Array(W * H);
  for (let i = 0; i < W * H; i++) {
    gray[i] = (data[i * 4] * 299 + data[i * 4 + 1] * 587 + data[i * 4 + 2] * 114) / 1000;
  }

  // 8 bits per row × 8 rows = 64 bits
  let bits = '';
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W - 1; x++) {
      bits += gray[y * W + x] > gray[y * W + x + 1] ? '1' : '0';
    }
  }
  // Pack 64 bits → 16 hex chars
  let hex = '';
  for (let i = 0; i < 64; i += 4) {
    hex += parseInt(bits.slice(i, i + 4), 2).toString(16);
  }
  return hex;
}

/**
 * face_spread = (union of face bbox area) / (image area).
 * Capped at 1. High values (>0.5) signal multi-face / group photos that
 * the layout engine should fit-contain rather than crop.
 */
function computeFaceSpread(
  faces: { x: number; y: number; width: number; height: number }[],
  imgW: number,
  imgH: number,
): number {
  if (!faces.length || !imgW || !imgH) return 0;
  // Bounding box of all faces (cheap conservative union)
  const minX = Math.min(...faces.map((f) => f.x));
  const minY = Math.min(...faces.map((f) => f.y));
  const maxX = Math.max(...faces.map((f) => f.x + f.width));
  const maxY = Math.max(...faces.map((f) => f.y + f.height));
  const bboxW = Math.max(0, maxX - minX);
  const bboxH = Math.max(0, maxY - minY);
  return Math.min(1, (bboxW * bboxH) / (imgW * imgH));
}

function loadImageFromUrl(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image load failed'));
    img.src = url;
  });
}

/**
 * Load an image with a CORS-safe fallback. Tries `<img crossOrigin="anonymous">`
 * first (fast, no extra fetch). If that errors — typically because the CDN
 * (e.g. imagedelivery.net for CF Images) doesn't return CORS headers — falls
 * back to `fetch → blob → object URL`, which works for any same-or-different
 * origin response we can read.
 */
async function loadImage(url: string): Promise<HTMLImageElement> {
  try {
    return await loadImageFromUrl(url);
  } catch {
    // Fallback: fetch the bytes ourselves, then create an object URL.
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error(`fetch failed ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      return await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('blob image load failed'));
        img.src = objectUrl;
      });
    } finally {
      // Don't revoke immediately — face-api re-reads the image during detection.
      // Revoke after a generous delay.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    }
  }
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

async function postMetadata(photoId: string, body: Record<string, unknown>) {
  await authedFetch(`/photos/${photoId}/metadata`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Faces on the photo EXACTLY AS IT IS.
 *
 * ⚠️ NO AUTOMATIC ROTATION (2026-09-26, #1215). This used to try 0°/90°/180°/
 * 270°, keep whichever made face-api most confident, and upload the rotated
 * copy. It turned upright photos sideways — four in #1215 (a woman standing at
 * a shooting range among them), three in #MAN-418282 — and it rotated the
 * 1400 px quality-62 DISPLAY copy, so an adopted rotation would have replaced
 * the full-resolution original (owner: « keep the high quality »). The
 * uploaded copy was never adopted anyway: the row's width/height were swapped
 * while the file stayed as it was, and the PDF printed those pages blank.
 * 597 such orphans exist since 10 July. EXIF orientation is baked at intake,
 * which is reliable; a truly sideways photo is turned with the editor's
 * rotate button, which replaces the file and its dimensions together.
 */
async function detectFacesAsIs(img: HTMLImageElement): Promise<{ x: number; y: number; width: number; height: number }[]> {
  const opts = new faceapi.SsdMobilenetv1Options({ minConfidence: 0.4 });
  const found = await faceapi.detectAllFaces(img, opts);
  return found.map((d) => ({
    x: Math.round(d.box.x), y: Math.round(d.box.y),
    width: Math.round(d.box.width), height: Math.round(d.box.height),
  }));
}

interface AnalyzeInput {
  photo_id: string;
  display_url: string;       // signed URL to the JPEG (preview or original)
  is_sideways?: boolean;     // Gemini vision flag — drop the 0° bias when true
}

/**
 * Analyze a single photo: load → face detection → compute metrics → POST.
 * Idempotent — caller is responsible for not re-running on already-analyzed
 * photos (check `analyzed_at !== null` before queuing).
 */
export async function analyzePhotoLazy(input: AnalyzeInput): Promise<void> {
  await sem.acquire();
  try {
    await loadModels();
    const img = await loadImage(input.display_url);

    // Try 0° first; if no faces, try 90° / 270° / 180°. Whichever orientation
    // finds the most faces wins. Photos without any faces stay at 0°.
    // Faces on the photo as it is — never rotated here (see detectFacesAsIs).
    const faces = await detectFacesAsIs(img);
    const phash = computeDHash(img);

    // ⚠️ THE FILE'S OWN DIMENSIONS — the only ones this function may ever post
    // (#MAN-418282 22 Sept, #1215 26 Sept: rotated dimensions on an unrotated
    // file blanked pages in print).
    await postMetadata(input.photo_id, {
      face_count: faces.length,
      face_data: faces,
      width: img.naturalWidth,
      height: img.naturalHeight,
      aspect_ratio: classifyAspect(img.naturalWidth, img.naturalHeight),
      face_spread: computeFaceSpread(faces, img.naturalWidth, img.naturalHeight),
      phash,
    });
  } catch (e) {
    // Mark as analyzed-with-zero-faces so we don't retry forever
    try {
      await postMetadata(input.photo_id, { face_count: 0, face_data: [] });
    } catch { /* swallow */ }
    console.warn('[lazyPhotoAnalysis] failed for', input.photo_id, (e as Error).message);
  } finally {
    sem.release();
  }
}

/**
 * Convenience: queue analysis for any photo lacking analyzed_at. Called by
 * the editor after the photos list loads. Fire-and-forget; the photos will
 * gain face_data + aspect_ratio + face_spread in the background.
 */
// Photos already force-rechecked for orientation this page load, so a stale
// is_sideways flag doesn't re-run face detection on every render.
const orientationRechecked = new Set<string>();

export function queueLazyAnalysis(
  photos: Array<{ id: string; signed_url: string | null; analyzed_at: string | null; is_sideways?: boolean }>,
): void {
  for (const p of photos) {
    if (!p.signed_url) continue;
    if (!p.analyzed_at) {
      // First-ever analysis — also runs the upright-rotation pass.
      void analyzePhotoLazy({ photo_id: p.id, display_url: p.signed_url, is_sideways: !!p.is_sideways });
    } else if (p.is_sideways && !orientationRechecked.has(p.id)) {
      // Already analyzed before the vision tagger ran; vision later flagged it
      // sideways. Re-run once this load to apply the rotation the first pass
      // missed (no-op if the photo is in fact already upright).
      orientationRechecked.add(p.id);
      void analyzePhotoLazy({ photo_id: p.id, display_url: p.signed_url, is_sideways: true });
    }
  }
}
